import React, { useContext } from "react"

import { useTranslation } from "react-i18next"

import { Grid } from "@mui/material"
import { Button, RadioButton } from "@synapse-analytics/synapse-ui"
import { useFormikContext } from "formik"
import type { Moment } from "moment"

import type { WorkflowVersionRetrieve } from "../../../types/generated/api/WorkflowVersionRetrieve"
import { WorkflowCanvasContext } from "../../Workflows"
import type { WorkflowSimulationFormik } from "../../Workflows/types/interfaces"
import { ExistingDatasetView } from "./ExistingDatasetView"
import { LiveDataView } from "./LiveDataView"
import { NewDatasetView } from "./NewDatasetView"

import styles from "./DataSource.module.scss"

/**
 * Simulation Data source selection screen
 *
 * @return {React.ReactElement}
 */
export function DataSource(): React.ReactElement {
  const { values, setFieldValue, validateForm } = useFormikContext<WorkflowSimulationFormik>()

  const { workflow } = useContext(WorkflowCanvasContext)

  const { t } = useTranslation("simulations", {
    keyPrefix: "simulationCreationDialog.dataSource",
  })

  const handleSettingDataFile = (uuid: string, columns: string[]): void => {
    setFieldValue("predictions_datafile", uuid)
    setFieldValue("datafile_columns", columns)

    setTimeout(() => {
      validateForm()
    }, 300)
  }

  return (
    <Grid container item xs={12} spacing={2}>
      {/* Radio Buttons */}
      <Grid container item xs={12} spacing={2}>
        {["Live Data", "New Dataset", "Existing Dataset"].map((item: string) => (
          <Grid item xs={4} key={item}>
            <Button
              variant={item === values.source ? "primary-inverse" : "secondary"}
              className={styles.radioBox}
              onClick={() => {
                setFieldValue("predictions_datafile", "")
                setFieldValue("datafile_columns", [""])
                setFieldValue("shouldProceed", true)
                setFieldValue("source", item)

                setTimeout(() => {
                  validateForm()
                }, 100)
              }}
              fullWidth
            >
              <RadioButton
                value={item}
                checked={item === values.source}
                label={t(item, { defaultValue: item })}
                id={item}
              />
            </Button>
          </Grid>
        ))}
      </Grid>

      {/* Data source options */}
      <Grid item container xs={12}>
        {values.source === "Live Data" && (
          <LiveDataView
            setFormikStartDate={(date: Moment | undefined) => setFieldValue("predictions_start_time", date)}
            setFormikEndDate={(date: Moment | undefined) => setFieldValue("predictions_end_time", date)}
            setFormikRequestsNumber={(data: number) => setFieldValue("totalRequestsNumber", data)}
          />
        )}
        {values.source === "New Dataset" && (
          <NewDatasetView
            setDataFile={handleSettingDataFile}
            workflow={workflow?.active_version as WorkflowVersionRetrieve}
          />
        )}
        {values.source === "Existing Dataset" && <ExistingDatasetView setDataFile={handleSettingDataFile} />}
      </Grid>
    </Grid>
  )
}
